/**
 * SlotsArchive — страница архива сгоревших нод.
 *
 * Доступна по роуту /slots/archive. Ссылка снизу страницы /slots
 * («Архив · N»). Показывает только контракты со status='burned':
 * имя ноды, стоимость, сколько заработано (= max_payout = 2.5x цены),
 * дата активации → дата сгорания.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'motion/react';
import { ArrowLeft, Flame } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { lunaApi } from '@/services/api';
import { useTelegramWebApp } from '@/hooks/useTelegramWebApp';
import { useUISounds } from '@/hooks/useUISounds';

interface BurnedContract {
  id: string;
  slotName: string;
  pricePaid: string;
  totalPaidOut: string;
  maxPayout: string;
  createdAt: string;
  burnedAt: string | null;
  hasPlasmaCore: boolean;
}

function formatDate(iso: string, lang: 'ru' | 'en'): string {
  const d = new Date(iso);
  if (lang === 'ru') {
    // ДД.ММ.ГГГГ (СНГ формат)
    return d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' });
  }
  return d.toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' });
}

function daysBetween(aIso: string, bIso: string | null): number {
  const a = new Date(aIso).getTime();
  const b = bIso ? new Date(bIso).getTime() : Date.now();
  return Math.max(1, Math.round((b - a) / 86_400_000));
}

function fmtAmount(n: number, lang: 'ru' | 'en'): string {
  return n.toLocaleString(lang === 'ru' ? 'ru-RU' : 'en-US', {
    maximumFractionDigits: 2,
  });
}

export default function SlotsArchive() {
  const { t, i18n } = useTranslation();
  const lang: 'ru' | 'en' = i18n.language === 'ru' ? 'ru' : 'en';
  const navigate = useNavigate();
  const { haptic } = useTelegramWebApp();
  const uiSounds = useUISounds();
  const [burned, setBurned] = useState<BurnedContract[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    lunaApi.slots
      .my()
      .then((res) => {
        const d = res.data as { success: boolean; contracts?: BurnedContract[] };
        if (d.success && d.contracts) {
          setBurned(
            d.contracts
              .filter((c) => (c as unknown as { status: string }).status === 'burned')
              .sort((a, b) => {
                const ta = a.burnedAt ? new Date(a.burnedAt).getTime() : 0;
                const tb = b.burnedAt ? new Date(b.burnedAt).getTime() : 0;
                return tb - ta;
              }),
          );
        }
      })
      .finally(() => setLoading(false));
  }, []);

  const totals = useMemo(() => {
    let totalInvested = 0;
    let totalEarned = 0;
    for (const c of burned) {
      totalInvested += parseFloat(c.pricePaid);
      totalEarned += parseFloat(c.totalPaidOut);
    }
    return { totalInvested, totalEarned, count: burned.length };
  }, [burned]);

  const handleBack = () => {
    haptic.light();
    uiSounds.tap();
    navigate('/slots');
  };

  return (
    <motion.div
      className="space-y-4 max-w-md mx-auto pt-2 pb-6"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
    >
      {/* Header with back button */}
      <div className="flex items-center gap-3 px-1">
        <button
          type="button"
          onClick={handleBack}
          className="w-9 h-9 rounded-full flex items-center justify-center lg-pill active:opacity-70 transition-opacity"
          aria-label="back"
        >
          <ArrowLeft size={18} strokeWidth={2.2} className="text-[var(--text-secondary)]" />
        </button>
        <div className="flex flex-col leading-tight">
          <span className="font-display font-extrabold text-[18px] text-[var(--text-primary)]">
            {t('archive.title')}
          </span>
          <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--text-muted)]">
            {t('archive.subtitle')}
          </span>
        </div>
      </div>

      {/* Summary */}
      {!loading && burned.length > 0 && (
        <div
          className="rounded-2xl px-4 py-3.5 grid grid-cols-3 gap-3"
          style={{
            background:
              'linear-gradient(180deg, var(--glass-fill-strong) 0%, var(--glass-fill) 100%)',
            border: '1px solid var(--glass-stroke)',
          }}
        >
          <SummaryCell label={t('archive.countLabel')} value={String(totals.count)} />
          <SummaryCell
            label={t('archive.investedLabel')}
            value={`${fmtAmount(totals.totalInvested, lang)} ₮`}
          />
          <SummaryCell
            label={t('archive.earnedLabel')}
            value={`+${fmtAmount(totals.totalEarned, lang)} ₮`}
            accent
          />
        </div>
      )}

      {/* Empty state */}
      {!loading && burned.length === 0 && (
        <div
          className="rounded-2xl px-6 py-10 text-center"
          style={{
            background: 'var(--glass-fill)',
            border: '1px solid var(--glass-stroke)',
          }}
        >
          <Flame size={32} strokeWidth={1.6} className="mx-auto text-[var(--text-muted)] mb-3" />
          <p className="text-[13px] text-[var(--text-secondary)] font-display font-bold">
            {t('archive.emptyTitle')}
          </p>
          <p className="text-[11px] text-[var(--text-muted)] mt-1.5 leading-[1.5]">
            {t('archive.emptyHint')}
          </p>
        </div>
      )}

      {/* Burned list */}
      {!loading && burned.length > 0 && (
        <div className="flex flex-col gap-2">
          {burned.map((c) => {
            const price = parseFloat(c.pricePaid);
            const earned = parseFloat(c.totalPaidOut);
            const roi = price > 0 ? ((earned / price - 1) * 100) : 0;
            const days = daysBetween(c.createdAt, c.burnedAt);
            return (
              <div
                key={c.id}
                className="rounded-2xl px-4 py-3"
                style={{
                  background:
                    'linear-gradient(180deg, var(--glass-fill-strong) 0%, var(--glass-fill) 100%)',
                  border: '1px solid var(--glass-stroke)',
                  boxShadow: 'var(--glass-shadow-soft)',
                }}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <span className="font-display font-bold text-[14px] text-[var(--text-primary)] truncate">
                        {c.slotName}
                      </span>
                      {c.hasPlasmaCore && (
                        <span className="font-mono text-[8.5px] uppercase tracking-[0.14em] px-1.5 py-[2px] rounded"
                          style={{
                            color: 'var(--accent-primary)',
                            background: 'color-mix(in oklab, var(--accent-primary) 14%, transparent)',
                          }}
                        >
                          PC
                        </span>
                      )}
                    </div>
                    <p className="font-mono text-[9.5px] uppercase tracking-[0.14em] text-[var(--text-muted)] mt-0.5">
                      {t('archive.priceLine', { price: fmtAmount(price, lang) })}
                    </p>
                  </div>
                  <div className="flex-shrink-0 text-right">
                    <p className="font-mono font-extrabold text-[13.5px] tabular-nums text-[var(--accent-primary)]">
                      +{fmtAmount(earned, lang)} ₮
                    </p>
                    <p className="font-mono text-[9px] uppercase tracking-[0.14em] text-[var(--text-muted)] mt-0.5">
                      ROI +{roi.toFixed(0)}%
                    </p>
                  </div>
                </div>

                <div className="mt-2.5 pt-2.5 border-t border-[var(--glass-stroke)] flex items-center justify-between text-[10px] font-mono uppercase tracking-[0.12em] text-[var(--text-muted)]">
                  <span>
                    {c.burnedAt
                      ? t('archive.burnedOn', { date: formatDate(c.burnedAt, lang) })
                      : ''}
                  </span>
                  <span>
                    {t('archive.lifespan', { days })}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {loading && (
        <div className="py-10 text-center text-[11px] text-[var(--text-muted)] font-mono">
          {t('archive.loading')}
        </div>
      )}
    </motion.div>
  );
}

function SummaryCell({
  label,
  value,
  accent,
}: {
  label: string;
  value: string;
  accent?: boolean;
}) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="font-mono text-[8.5px] uppercase tracking-[0.14em] text-[var(--text-muted)] font-bold">
        {label}
      </span>
      <span
        className={`font-display font-extrabold tabular-nums text-[14px] ${
          accent ? 'text-[var(--accent-primary)]' : 'text-[var(--text-primary)]'
        }`}
      >
        {value}
      </span>
    </div>
  );
}
